import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { AuthContext } from "./AuthContext";
import apiService from "../services/api.service";

/**
 * OPUS-913 — permission-aware gating for the dashboard.
 *
 * Replaces hard-coded role-name checks (`role === "super_admin"`) with a
 * `can(permission)` driven by the @ae/auth model. Effective permissions are
 * resolved the same way the backend does (roles.js `resolveEffectivePermissions`):
 *
 *   1. super_admin  -> ["*"] (everything)
 *   2. the user's DB role.permissions[] IF it already uses the new dotted
 *      vocabulary (so seeded/custom roles are honoured)
 *   3. otherwise the canonical defaults from GET /v1/permissions/catalog
 *      (roleDefaults = @ae/auth ROLE_PERMISSIONS), cached in localStorage so
 *      gating is instant on subsequent loads.
 *
 * This is UX gating only — the backend `authorize()` middleware is the real
 * boundary. While the catalog is still loading, `can()` is permissive (returns
 * true) so nothing flickers/locks; route + page guards should additionally check
 * `ready` before *removing* access.
 */
export const PermissionsContext = createContext({ can: () => true, permissions: [], ready: false });

const CACHE_KEY = "rbacRoleDefaults";
const ALIASES = {
  superadmin: "super_admin", "super-admin": "super_admin", sysadmin: "super_admin",
  orgadmin: "admin", org_admin: "admin",
};
const normalizeRole = (r) => {
  const name = typeof r === "object" ? (r?.name || r?.role || r?.key) : r;
  if (typeof name !== "string") return null;
  const k = name.trim().toLowerCase().replace(/\s+/g, "_");
  return ALIASES[k] || k;
};
const looksDotted = (arr) => Array.isArray(arr) && arr.some((p) => p === "*" || (typeof p === "string" && p.includes(".")));

/** Wildcard / exact / prefix (`renderers.*`) match — mirrors @ae/auth permissionsInclude. */
const matches = (perms, perm) => {
  if (!perms || !perms.length) return false;
  if (perms.includes("*")) return true;
  if (perms.includes(perm)) return true;
  return perms.some((p) => typeof p === "string" && p.endsWith(".*") && perm.startsWith(p.slice(0, -1)));
};

export const PermissionsProvider = ({ children }) => {
  const { userInfo } = useContext(AuthContext);
  const role = userInfo?.data?.role?.name;
  const roleKey = normalizeRole(role);
  const dbPerms = userInfo?.data?.role?.permissions;
  // Server-resolved union of role permissions + direct/user-level entitlement
  // grants (see resolveEffectivePermissions in @ae/auth roles.js). This is what
  // login/session endpoints already populate — prefer it over role.permissions,
  // which can never reflect a direct grant.
  const directPerms = userInfo?.data?.permissions;

  const [roleDefaults, setRoleDefaults] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CACHE_KEY)) || null; } catch (_) { return null; }
  });

  useEffect(() => {
    if (!userInfo || roleKey === "super_admin") return undefined; // super_admin needs no catalog
    let cancelled = false;
    apiService.getPermissionCatalog()
      .then((resp) => {
        const rd = resp?.data?.roleDefaults || resp?.roleDefaults;
        if (rd && !cancelled) {
          setRoleDefaults(rd);
          try { localStorage.setItem(CACHE_KEY, JSON.stringify(rd)); } catch (_) { /* ignore */ }
        }
      })
      .catch(() => { /* keep cached/role fallback */ });
    return () => { cancelled = true; };
  }, [userInfo, roleKey]);

  const permissions = useMemo(() => {
    if (!userInfo) return [];
    if (roleKey === "super_admin") return ["*"];
    if (looksDotted(directPerms)) return directPerms;         // server-resolved union (role ∪ direct grants)
    if (looksDotted(dbPerms)) return dbPerms;                 // fallback: honour seeded/custom role perms
    if (roleDefaults && roleKey && roleDefaults[roleKey]) return roleDefaults[roleKey];
    return [];
  }, [userInfo, roleKey, directPerms, dbPerms, roleDefaults]);

  // "ready" once we can resolve permissions authoritatively (super_admin, dotted
  // direct/DB perms, or the catalog has loaded). Guards should treat !ready as
  // "don't remove access yet" to avoid load-time flicker/bounce.
  const ready = roleKey === "super_admin" || looksDotted(directPerms) || looksDotted(dbPerms) || !!roleDefaults;

  const can = useCallback((perm) => {
    if (!ready) return true; // permissive until resolved (backend still enforces)
    const list = Array.isArray(perm) ? perm : [perm];
    return list.some((p) => matches(permissions, p));
  }, [ready, permissions]);

  const value = useMemo(() => ({ can, permissions, ready }), [can, permissions, ready]);
  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export const usePermissions = () => useContext(PermissionsContext);
